import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "Campeggio Verde Mare",
        metaTitle: "Campeggio Verde Mare | Piazzole e bungalow",
        metaDescription: "Piazzole ombreggiate, bungalow e servizi comodi. Contattaci per disponibilità.",
        intro: "Ombra naturale, mare vicino e spazi comodi. Una vacanza semplice e autentica.",
        menu: [
            { label: "Tariffe", href: "#tariffe" },
            { label: "Servizi", href: "#servizi" },
            { label: "Contatto", href: "#contatto" }
        ],
        ctaPrimary: "Vedi tariffe",
        ctaSecondary: "Richiedi disponibilità",
        servicesTitle: "Servizi principali",
        servicesList: [
            "Docce calde e lavanderia",
            "Market e area barbecue",
            "Navetta per la spiaggia"
        ],
        ratesTitle: "Tariffe estive",
        rates: [
            { title: "Piazzola standard", body: "Da €25 a notte" },
            { title: "Piazzola ombreggiata", body: "Da €35 a notte" },
            { title: "Piazzola camper", body: "Da €40 a notte" },
            { title: "Roulotte attrezzata", body: "Da €60 a notte" },
            { title: "Bungalow", body: "Da €70 a notte" }
        ],
        calendarTitle: "Calendario disponibilità",
        calendarSubtitle: "Seleziona le date per vedere gli slot disponibili.",
        calendarLegendAvailable: "Disponibile",
        calendarLegendBusy: "Occupato",
        calendarLegendSelected: "Selezionato",
        calendarNote: "Calendario dimostrativo: il form non invia prenotazioni.",
        calendarMonth: "Agosto 2026",
        featuresTitle: "Cosa trovi",
        features: [
            "Area giochi e attività per bambini",
            "Escursioni in barca su richiesta",
            "Noleggio bici e kayak",
            "Bar con prodotti locali"
        ],
        contactTitle: "Richiedi disponibilità",
        contactCta1: "Contattaci",
        contactCta2: "Chiama ora"
    },
    en: {
        title: "Verde Mare Camping",
        metaTitle: "Verde Mare Camping | Pitches and bungalows",
        metaDescription: "Shaded pitches, bungalows and handy services. Contact us for availability.",
        intro: "Natural shade, the sea nearby and comfortable spaces. A simple, authentic holiday.",
        menu: [
            { label: "Rates", href: "#tariffe" },
            { label: "Services", href: "#servizi" },
            { label: "Contact", href: "#contatto" }
        ],
        ctaPrimary: "See rates",
        ctaSecondary: "Check availability",
        servicesTitle: "Main services",
        servicesList: [
            "Hot showers and laundry",
            "Market and barbecue area",
            "Shuttle to the beach"
        ],
        ratesTitle: "Summer rates",
        rates: [
            { title: "Standard pitch", body: "From €25 per night" },
            { title: "Shaded pitch", body: "From €35 per night" },
            { title: "Camper pitch", body: "From €40 per night" },
            { title: "Equipped caravan", body: "From €60 per night" },
            { title: "Bungalow", body: "From €70 per night" }
        ],
        calendarTitle: "Availability calendar",
        calendarSubtitle: "Pick dates to see available slots.",
        calendarLegendAvailable: "Available",
        calendarLegendBusy: "Booked",
        calendarLegendSelected: "Selected",
        calendarNote: "Demo calendar: the form does not submit bookings.",
        calendarMonth: "August 2026",
        featuresTitle: "What you get",
        features: [
            "Kids playground and activities",
            "Boat trips on request",
            "Bike and kayak rental",
            "Bar with local products"
        ],
        contactTitle: "Check availability",
        contactCta1: "Contact us",
        contactCta2: "Call now"
    },
    de: {
        title: "Camping Verde Mare",
        metaTitle: "Camping Verde Mare | Stellplätze und Bungalows",
        metaDescription: "Schattige Stellplätze, Bungalows und praktische Services. Kontaktieren Sie uns.",
        intro: "Natürlicher Schatten, das Meer in der Nähe und viel Platz. Ein einfacher, echter Urlaub.",
        menu: [
            { label: "Preise", href: "#tariffe" },
            { label: "Services", href: "#servizi" },
            { label: "Kontakt", href: "#contatto" }
        ],
        ctaPrimary: "Preise ansehen",
        ctaSecondary: "Verfügbarkeit prüfen",
        servicesTitle: "Wichtige Services",
        servicesList: [
            "Warme Duschen und Wäscherei",
            "Market und Grillplatz",
            "Shuttle zum Strand"
        ],
        ratesTitle: "Sommerpreise",
        rates: [
            { title: "Standard‑Stellplatz", body: "Ab €25 pro Nacht" },
            { title: "Schattiger Stellplatz", body: "Ab €35 pro Nacht" },
            { title: "Wohnmobil‑Stellplatz", body: "Ab €40 pro Nacht" },
            { title: "Ausgestatteter Wohnwagen", body: "Ab €60 pro Nacht" },
            { title: "Bungalow", body: "Ab €70 pro Nacht" }
        ],
        calendarTitle: "Verfügbarkeitskalender",
        calendarSubtitle: "Wähle Daten, um freie Slots zu sehen.",
        calendarLegendAvailable: "Frei",
        calendarLegendBusy: "Belegt",
        calendarLegendSelected: "Ausgewählt",
        calendarNote: "Demo‑Kalender: das Formular sendet keine Buchungen.",
        calendarMonth: "August 2026",
        featuresTitle: "Was Sie bekommen",
        features: [
            "Spielplatz und Kinderaktivitäten",
            "Bootsausflüge auf Anfrage",
            "Fahrrad- und Kajakverleih",
            "Bar mit lokalen Produkten"
        ],
        contactTitle: "Verfügbarkeit anfragen",
        contactCta1: "Kontakt",
        contactCta2: "Jetzt anrufen"
    }
};

const CampeggioPage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/campeggio"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-gradient-to-b from-emerald-200 via-white to-lime-100 text-emerald-950">
                <header className="px-6 pt-10 pb-10 md:px-12 lg:px-20">
                    <ExampleLandingHeader title={t.title} items={t.menu} />
                    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_0.9fr]">
                        <div>
                            <p className="text-lg text-emerald-900">{t.intro}</p>
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a href="#tariffe" className="rounded-full bg-emerald-700 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200">
                                    {t.ctaPrimary}
                                </a>
                                <a href="#contatto" className="rounded-full border border-emerald-700 px-6 py-3 text-sm font-semibold text-emerald-700">
                                    {t.ctaSecondary}
                                </a>
                            </div>
                        </div>
                        <div id="servizi" className="rounded-3xl bg-white p-6 shadow-lg">
                            <h2 className="text-xl font-semibold">{t.servicesTitle}</h2>
                            <ul className="mt-4 space-y-3 text-emerald-800">
                                {t.servicesList.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="mt-8 overflow-hidden rounded-3xl shadow-lg">
                        <img
                            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1600&auto=format&fit=crop"
                            alt="Tenda in riva al mare al tramonto"
                            className="h-64 w-full object-cover md:h-96"
                            loading="lazy"
                        />
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="tariffe" className="rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.ratesTitle}</h2>
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            {t.rates.map((item) => (
                                <div key={item.title} className="rounded-2xl bg-emerald-50 p-4">
                                    <p className="font-semibold">{item.title}</p>
                                    <p className="mt-2 text-sm text-emerald-700">{item.body}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                    <section className="mt-10 rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <h2 className="text-2xl font-semibold">{t.calendarTitle}</h2>
                                <p className="mt-2 text-sm text-emerald-700">{t.calendarSubtitle}</p>
                            </div>
                            <div className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs text-emerald-700">
                                {t.calendarMonth}
                            </div>
                        </div>
                        <div className="mt-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
                            <div className="grid grid-cols-7 gap-2 text-center text-xs text-stone-600">
                                {["L", "M", "M", "G", "V", "S", "D"].map((day) => (
                                    <div key={day} className="rounded-lg bg-emerald-50 py-2 font-semibold text-emerald-700">
                                        {day}
                                    </div>
                                ))}
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => {
                                    const isBusy = [2, 3, 4, 11, 12, 13, 18, 19, 20, 26, 27].includes(date);
                                    const isSelected = [21, 22, 23, 24].includes(date);
                                    const baseClass = "rounded-lg py-3 font-semibold";
                                    const stateClass = isSelected
                                        ? "bg-emerald-600 text-white"
                                        : isBusy
                                          ? "bg-stone-200 text-stone-500"
                                          : "bg-white text-stone-700 border border-emerald-100";
                                    return (
                                        <div key={date} className={`${baseClass} ${stateClass}`}>
                                            {date}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="rounded-3xl bg-emerald-50 p-6">
                                <h3 className="text-lg font-semibold text-emerald-800">Richiesta di prenotazione</h3>
                                <div className="mt-4 grid gap-3 text-sm">
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 text-emerald-700">Arrivo</div>
                                        <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 text-emerald-700">Partenza</div>
                                    </div>
                                    <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 text-emerald-700">Ospiti / Mezzo</div>
                                    <button type="button" className="rounded-full bg-emerald-700 px-6 py-3 text-sm font-semibold text-white">
                                        Richiedi disponibilità
                                    </button>
                                </div>
                                <p className="mt-4 text-xs text-emerald-700">{t.calendarNote}</p>
                                <div className="mt-4 flex flex-wrap gap-3 text-xs text-emerald-700">
                                    <span className="flex items-center gap-2">
                                        <span className="h-3 w-3 rounded bg-white border border-emerald-200"></span>
                                        {t.calendarLegendAvailable}
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <span className="h-3 w-3 rounded bg-stone-200"></span>
                                        {t.calendarLegendBusy}
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <span className="h-3 w-3 rounded bg-emerald-600"></span>
                                        {t.calendarLegendSelected}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <img
                            src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1400&auto=format&fit=crop"
                            alt="Tende in campeggio al tramonto"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?q=80&w=1400&auto=format&fit=crop"
                            alt="Bungalow immersi nella natura"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                    </div>

                    <section className="mt-10 rounded-3xl bg-emerald-900 p-6 text-white shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.featuresTitle}</h2>
                        <div className="mt-6 grid gap-4 md:grid-cols-2">
                            {t.features.map((item) => (
                                <div key={item} className="rounded-2xl bg-emerald-800 p-4">{item}</div>
                            ))}
                        </div>
                    </section>

                    <SocialLinksSection className="mt-10" />

                    <section id="contatto" className="mt-10 rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.contactTitle}</h2>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href="/estate-2026#contatto" className="rounded-full bg-emerald-700 px-6 py-3 text-sm font-semibold text-white">
                                {t.contactCta1}
                            </a>
                            <a href="tel:+393515699197" className="rounded-full border border-emerald-700 px-6 py-3 text-sm font-semibold text-emerald-700">
                                {t.contactCta2}
                            </a>
                        </div>
                    </section>
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default CampeggioPage;
